/**
 * KitchenDisplayTab - Kitchen Display System for managing orders
 * Real-time order queue with ticket times and bump functionality
 */
import { useState, useEffect, useRef } from 'react';
import { 
  ChefHat, Clock, Check, RotateCcw, AlertTriangle, 
  Play, Pause, Volume2, VolumeX, RefreshCw, Utensils
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

const STATUS_COLORS = {
  pending: 'border-amber-500 bg-amber-50 dark:bg-amber-950/30',
  'in-progress': 'border-blue-500 bg-blue-50 dark:bg-blue-950/30',
  completed: 'border-green-500 bg-green-50 dark:bg-green-950/30'
};

const PRIORITY_BADGES = {
  rush: 'bg-red-500 text-white',
  vip: 'bg-purple-500 text-white',
  normal: 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
};

export default function KitchenDisplayTab({ authHeaders }) {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const audioRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    fetchTickets();
    fetchStats();
    
    // Setup polling (fallback for WebSocket)
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchTickets();
        fetchStats();
      }, 10000); // Refresh every 10 seconds
    }
    
    // Copy wsRef to local variable for cleanup
    const ws = wsRef.current;
    
    return () => {
      if (interval) clearInterval(interval);
      if (ws) ws.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  const fetchTickets = async () => {
    try {
      const res = await api.get('/kds/tickets');
      const newTickets = res.data.tickets || [];
      
      // Play sound for new tickets
      if (soundEnabled && tickets.length > 0 && newTickets.length > tickets.length) {
        playNotificationSound();
      }
      
      setTickets(newTickets);
    } catch (err) {
      console.error('Failed to fetch KDS tickets:', err);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/kds/stats?period=today');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch KDS stats:', err);
    }
  };

  const playNotificationSound = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log('Audio play failed:', e));
    }
  };

  const startTicket = async (orderId) => {
    try {
      await api.post(`/kds/tickets/${orderId}/start`);
      fetchTickets();
      toast.success('Ticket started');
    } catch (err) {
      toast.error('Failed to start ticket');
    }
  };

  const bumpTicket = async (orderId) => {
    try {
      await api.post(`/kds/tickets/${orderId}/bump`);
      fetchTickets();
      fetchStats();
      toast.success('Order completed!');
    } catch (err) {
      toast.error('Failed to bump ticket');
    }
  };

  const recallTicket = async (orderId) => {
    try {
      await api.post(`/kds/tickets/${orderId}/recall`);
      fetchTickets();
      toast.success('Ticket recalled');
    } catch (err) {
      toast.error('Failed to recall ticket');
    }
  };

  const getWaitTimeColor = (waitTime, isLate) => {
    if (isLate) return 'text-red-600 dark:text-red-400';
    if (waitTime > 5) return 'text-amber-600 dark:text-amber-400';
    return 'text-green-600 dark:text-green-400';
  };

  const formatTime = (minutes) => {
    if (minutes < 1) return '<1 min';
    if (minutes < 60) return `${minutes} min`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kitchen-display-tab">
      {/* Hidden audio element for notifications */}
      <audio ref={audioRef} src="data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleDoAAABbpuv/tl4PH3Ouw/92GwAANZDi/6ZjAAApm9z/q2sAADGU3v+oZwAALJXg/6hqAAAulN//qWgAAC6V3/+oaAAALpXf/6hoAAAuld//qGgAAC6V3/+oaA==" />
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <ChefHat className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">Kitchen Display</h2>
            <p className="text-muted-foreground text-sm">Real-time order queue</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className="rounded-full"
          >
            {autoRefresh ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="rounded-full"
          >
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchTickets(); fetchStats(); }}
            className="rounded-full"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-foreground">{stats.tickets_pending || 0}</div>
            <div className="text-sm text-amber-600">Pending</div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-foreground">{stats.tickets_in_progress || 0}</div>
            <div className="text-sm text-blue-600">In Progress</div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-foreground">{stats.tickets_completed || 0}</div>
            <div className="text-sm text-green-600">Completed Today</div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-foreground">{stats.avg_ticket_time || 0}</div>
            <div className="text-sm text-muted-foreground">Avg Time (min)</div>
          </div>
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-foreground">{stats.throughput_per_hour || 0}</div>
            <div className="text-sm text-muted-foreground">Orders/Hour</div>
          </div>
        </div>
      )}

      {/* Tickets Grid */}
      {tickets.length === 0 ? (
        <div className="bg-card rounded-2xl p-12 border border-border text-center">
          <Utensils className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Active Orders</h3>
          <p className="text-muted-foreground">New orders will appear here automatically</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {tickets.map((ticket) => (
            <div
              key={ticket.order_id}
              className={`rounded-2xl border-2 p-4 transition-all ${STATUS_COLORS[ticket.status]} ${ticket.is_late ? 'animate-pulse' : ''}`}
              data-testid={`kds-ticket-${ticket.order_id}`}
            >
              {/* Ticket Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-foreground">
                    #{ticket.order_number}
                  </span>
                  {ticket.priority !== 'normal' && (
                    <span className={`text-xs px-2 py-0.5 rounded-full ${PRIORITY_BADGES[ticket.priority]}`}>
                      {ticket.priority.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className={`flex items-center gap-1 ${getWaitTimeColor(ticket.wait_time, ticket.is_late)}`}>
                  <Clock className="w-4 h-4" />
                  <span className="font-mono font-bold">{formatTime(ticket.wait_time)}</span>
                  {ticket.is_late && <AlertTriangle className="w-4 h-4" />}
                </div>
              </div>

              {/* Customer & Order Type */}
              <div className="mb-3">
                <div className="font-medium text-foreground">{ticket.customer_name}</div>
                <div className="text-sm text-muted-foreground capitalize">
                  {ticket.order_type}
                  {ticket.table_number && ` • Table ${ticket.table_number}`}
                </div>
              </div>

              {/* Items */}
              <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                {ticket.items.map((item, idx) => (
                  <div key={idx} className="bg-white/50 dark:bg-black/20 rounded-lg p-2">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-foreground">
                          {item.quantity}x {item.name}
                        </div>
                        {item.modifiers?.length > 0 && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {item.modifiers.map(m => m.selected_option || m.name).join(', ')}
                          </div>
                        )}
                        {item.extras?.length > 0 && (
                          <div className="text-xs text-green-600 dark:text-green-400">
                            +{item.extras.map(e => e.name || e).join(', ')}
                          </div>
                        )}
                        {item.removed_ingredients?.length > 0 && (
                          <div className="text-xs text-red-600 dark:text-red-400">
                            No {item.removed_ingredients.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Special Instructions */}
              {ticket.special_instructions && (
                <div className="bg-amber-100 dark:bg-amber-900/30 rounded-lg p-2 mb-4 text-sm text-amber-800 dark:text-amber-200">
                  <strong>Note:</strong> {ticket.special_instructions}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                {ticket.status === 'pending' && (
                  <Button
                    onClick={() => startTicket(ticket.order_id)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid={`start-${ticket.order_id}`}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start
                  </Button>
                )}
                {ticket.status === 'in-progress' && (
                  <Button
                    onClick={() => bumpTicket(ticket.order_id)}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid={`bump-${ticket.order_id}`}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Bump
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => bumpTicket(ticket.order_id)}
                  className="shrink-0"
                  title="Quick Bump"
                >
                  <Check className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Stats Footer */}
      {stats && stats.longest_wait_minutes > 0 && (
        <div className={`rounded-xl p-4 ${stats.longest_wait_minutes > 15 ? 'bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800' : 'bg-muted/50'}`}>
          <div className="flex items-center gap-2">
            {stats.longest_wait_minutes > 15 && <AlertTriangle className="w-5 h-5 text-red-500" />}
            <span className="text-sm">
              <strong>Longest wait:</strong> {formatTime(stats.longest_wait_minutes)}
              {stats.longest_wait_minutes > 15 && ' - needs attention!'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
